import { createGlobalStyle } from 'styled-components';
import { designTokens } from './designTokens';

export const GlobalStyles = createGlobalStyle`
  /* Import Google Fonts */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

  /* Reset and base styles */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: ${designTokens.typography.fontFamily.primary};
    font-size: ${designTokens.typography.fontSize.base};
    font-weight: ${designTokens.typography.fontWeight.normal};
    line-height: ${designTokens.typography.lineHeight.normal};
    color: ${designTokens.colors.text.primary};
    background-color: ${designTokens.colors.background.secondary};
    overflow-x: hidden;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${designTokens.typography.fontFamily.secondary};
    font-weight: ${designTokens.typography.fontWeight.bold};
    line-height: ${designTokens.typography.lineHeight.tight};
    color: ${designTokens.colors.text.primary};
    margin-bottom: ${designTokens.spacing[4]};
  }

  h1 {
    font-size: ${designTokens.typography.fontSize['4xl']};
    font-weight: ${designTokens.typography.fontWeight.extrabold};
  }

  h2 {
    font-size: ${designTokens.typography.fontSize['3xl']};
    font-weight: ${designTokens.typography.fontWeight.bold};
  }

  h3 {
    font-size: ${designTokens.typography.fontSize['2xl']};
    font-weight: ${designTokens.typography.fontWeight.semibold};
  }

  h4 {
    font-size: ${designTokens.typography.fontSize.xl};
    font-weight: ${designTokens.typography.fontWeight.semibold};
  }

  h5 {
    font-size: ${designTokens.typography.fontSize.lg};
    font-weight: ${designTokens.typography.fontWeight.medium};
  }

  h6 {
    font-size: ${designTokens.typography.fontSize.base};
    font-weight: ${designTokens.typography.fontWeight.medium};
  }

  p {
    margin-bottom: ${designTokens.spacing[4]};
    color: ${designTokens.colors.text.secondary};
    line-height: ${designTokens.typography.lineHeight.relaxed};
  }

  /* Links */
  a {
    color: ${designTokens.colors.primary[500]};
    text-decoration: none;
    transition: ${designTokens.transitions.fast};
    
    &:hover {
      color: ${designTokens.colors.primary[600]};
      text-decoration: underline;
    }
  }

  /* Buttons */
  button {
    font-family: inherit;
    font-size: inherit;
    border: none;
    background: none;
    cursor: pointer;
    transition: ${designTokens.transitions.fast};
  }

  /* Form elements */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: 1px solid ${designTokens.colors.border.medium};
    border-radius: ${designTokens.borderRadius.lg};
    padding: ${designTokens.spacing[3]} ${designTokens.spacing[4]};
    background-color: ${designTokens.colors.surface.primary};
    color: ${designTokens.colors.text.primary};
    transition: ${designTokens.transitions.fast};
    
    &:focus {
      outline: none;
      border-color: ${designTokens.colors.primary[500]};
      box-shadow: 0 0 0 3px ${designTokens.colors.primary[100]};
    }
    
    &::placeholder {
      color: ${designTokens.colors.text.tertiary};
    }
  }

  /* Cards */
  .card {
    background: ${designTokens.colors.surface.primary};
    border-radius: ${designTokens.borderRadius.xl};
    box-shadow: ${designTokens.shadows.md};
    border: 1px solid ${designTokens.colors.border.light};
    padding: ${designTokens.spacing[6]};
    transition: ${designTokens.transitions.fast};
    
    &:hover {
      box-shadow: ${designTokens.shadows.lg};
      transform: translateY(-2px);
    }
  }

  /* Utility classes */
  .text-primary {
    color: ${designTokens.colors.text.primary};
  }

  .text-secondary {
    color: ${designTokens.colors.text.secondary};
  }

  .text-tertiary {
    color: ${designTokens.colors.text.tertiary};
  }

  .text-green {
    color: ${designTokens.riverPoultry.brandColors.green};
  }

  .text-red {
    color: ${designTokens.riverPoultry.brandColors.red};
  }

  .text-orange {
    color: ${designTokens.riverPoultry.brandColors.orange};
  }

  .bg-primary {
    background-color: ${designTokens.colors.primary[500]};
  }

  .bg-secondary {
    background-color: ${designTokens.colors.secondary[500]};
  }

  .bg-green {
    background-color: ${designTokens.riverPoultry.brandColors.green};
  }

  .bg-red {
    background-color: ${designTokens.riverPoultry.brandColors.red};
  }

  .bg-orange {
    background-color: ${designTokens.riverPoultry.brandColors.orange};
  }

  .gradient-primary {
    background: ${designTokens.riverPoultry.gradients.primary};
  }

  .gradient-secondary {
    background: ${designTokens.riverPoultry.gradients.secondary};
  }

  .gradient-accent {
    background: ${designTokens.riverPoultry.gradients.accent};
  }

  /* Spacing utilities */
  .p-0 { padding: 0; }
  .p-1 { padding: ${designTokens.spacing[1]}; }
  .p-2 { padding: ${designTokens.spacing[2]}; }
  .p-3 { padding: ${designTokens.spacing[3]}; }
  .p-4 { padding: ${designTokens.spacing[4]}; }
  .p-5 { padding: ${designTokens.spacing[5]}; }
  .p-6 { padding: ${designTokens.spacing[6]}; }
  .p-8 { padding: ${designTokens.spacing[8]}; }

  .m-0 { margin: 0; }
  .m-1 { margin: ${designTokens.spacing[1]}; }
  .m-2 { margin: ${designTokens.spacing[2]}; }
  .m-3 { margin: ${designTokens.spacing[3]}; }
  .m-4 { margin: ${designTokens.spacing[4]}; }
  .m-5 { margin: ${designTokens.spacing[5]}; }
  .m-6 { margin: ${designTokens.spacing[6]}; }
  .m-8 { margin: ${designTokens.spacing[8]}; }

  /* Border radius utilities */
  .rounded-sm { border-radius: ${designTokens.borderRadius.sm}; }
  .rounded { border-radius: ${designTokens.borderRadius.base}; }
  .rounded-md { border-radius: ${designTokens.borderRadius.md}; }
  .rounded-lg { border-radius: ${designTokens.borderRadius.lg}; }
  .rounded-xl { border-radius: ${designTokens.borderRadius.xl}; }
  .rounded-2xl { border-radius: ${designTokens.borderRadius['2xl']}; }
  .rounded-full { border-radius: ${designTokens.borderRadius.full}; }

  /* Shadow utilities */
  .shadow-sm { box-shadow: ${designTokens.shadows.sm}; }
  .shadow { box-shadow: ${designTokens.shadows.base}; }
  .shadow-md { box-shadow: ${designTokens.shadows.md}; }
  .shadow-lg { box-shadow: ${designTokens.shadows.lg}; }
  .shadow-xl { box-shadow: ${designTokens.shadows.xl}; }

  /* Mobile responsive utilities */
  ${designTokens.mediaQueries.maxMd} {
    .mobile-hidden {
      display: none !important;
    }
    
    .mobile-full-width {
      width: 100% !important;
    }
    
    .mobile-text-sm {
      font-size: ${designTokens.mobileTypography.fontSize.sm} !important;
    }
    
    .mobile-text-base {
      font-size: ${designTokens.mobileTypography.fontSize.base} !important;
    }
    
    .mobile-p-4 {
      padding: ${designTokens.mobileSpacing[4]} !important;
    }
    
    .mobile-p-6 {
      padding: ${designTokens.mobileSpacing[6]} !important;
    }
  }

  /* Desktop utilities */
  ${designTokens.mediaQueries.minLg} {
    .desktop-hidden {
      display: none !important;
    }
  }

  /* Touch-friendly button sizing for mobile */
  ${designTokens.mediaQueries.maxMd} {
    button, .btn {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
  }

  /* Focus styles for accessibility */
  *:focus-visible {
    outline: 2px solid ${designTokens.colors.primary[500]};
    outline-offset: 2px;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${designTokens.colors.background.tertiary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${designTokens.colors.border.medium};
    border-radius: ${designTokens.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${designTokens.colors.border.dark};
  }

  /* Animation utilities */
  .animate-fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }

  .animate-slide-up {
    animation: slideUp 0.3s ease-out;
  }

  .animate-slide-down {
    animation: slideDown 0.3s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    
    a, a:visited {
      text-decoration: underline;
    }
    
    .no-print {
      display: none !important;
    }
  }
`;

export default GlobalStyles;
